// <copyright file="ConfigLoader.ts">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import urlJoin from "url-join";

export interface IConfig {
    title: string;
    mapCenter: { lat: number, lon: number };
    loginRedirectUrl?: string;
    logoutRedirectUrl?: string;
    cognitoAuth: { cognitoAuthUrl: string; cognitoUserPoolId: string };
    apiBaseUrl?: string;
    cvdfApiBaseUrl?: string;
    cvdfRealTimeBaseUrl?: string;
    featureFlags: { name: string; enabled: boolean }[];
    logos?: { owner: string; application: string };
    identityProviders: {
        name: string;
        enabled: boolean;
        displayName: string;
        clientId: string;
        scope: string;
    }[];
    theme?: string;
    footer?: {
        usefulLinks: { text: string; url: string }[];
        contactUs: string[];
    };
}

export class Config implements IConfig {
    title = "<TITLE>";
    mapCenter = { lat: 0, lon: 0 };
    loginRedirectUrl = "<LOGIN-URL>";
    logoutRedirectUrl = "<LOGOUT-URL>";
    cognitoAuth: { cognitoAuthUrl: string; cognitoUserPoolId: string } = {
        cognitoAuthUrl: "",
        cognitoUserPoolId: "",
    };
    apiBaseUrl = "<API-URL>";
    cvdfApiBaseUrl = "<CVDF-API-URL>";
    cvdfRealTimeBaseUrl = "<CVDF-REALTIME-API-URL>";
    featureFlags: { name: string; enabled: boolean }[] = [];
    logos: { owner: string; application: string } = {
        owner: "<LOGO-URI>",
        application: "<LOGO-URI>",
    };
    identityProviders: {
        name: string;
        enabled: boolean;
        displayName: string;
        clientId: string;
        scope: string;
    }[] = [];
    theme = "<THEME-CSS-URI>";
    footer = {
        usefulLinks: [{ text: "", url: "" }],
        contactUs: [],
    };

    constructor(config: IConfig) {
        Object.assign(this, config);
    }

    static fetch = async () => {
        const defaultUrl = "http://localhost:3000";
        let resourcesBaseUrl = defaultUrl;
        console.debug(
            `REACT_RESOURCES_BASE_URL: ${window.__RUNTIME_CONFIG__.REACT_RESOURCES_BASE_URL}`
        );
        if (window.__RUNTIME_CONFIG__.REACT_RESOURCES_BASE_URL) {
            resourcesBaseUrl =
                window.__RUNTIME_CONFIG__.REACT_RESOURCES_BASE_URL;
        }

        let loginRedirectUrl = defaultUrl;
        console.debug(
            `REACT_LOGIN_REDIRECT_URL: ${window.__RUNTIME_CONFIG__.REACT_LOGIN_REDIRECT_URL}`
        );
        if (window.__RUNTIME_CONFIG__.REACT_LOGIN_REDIRECT_URL) {
            loginRedirectUrl =
                window.__RUNTIME_CONFIG__.REACT_LOGIN_REDIRECT_URL;
        }

        let logoutRedirectUrl = defaultUrl;
        console.debug(
            `REACT_LOGOUT_REDIRECT_URL:${window.__RUNTIME_CONFIG__.REACT_LOGOUT_REDIRECT_URL}`
        );
        if (window.__RUNTIME_CONFIG__.REACT_LOGOUT_REDIRECT_URL) {
            logoutRedirectUrl =
                window.__RUNTIME_CONFIG__.REACT_LOGOUT_REDIRECT_URL;
        }

        let apiBaseUrl = defaultUrl;
        console.debug(
            `REACT_API_BASE_URL: ${window.__RUNTIME_CONFIG__.REACT_API_BASE_URL}`
        );
        if (window.__RUNTIME_CONFIG__.REACT_API_BASE_URL) {
            apiBaseUrl = window.__RUNTIME_CONFIG__.REACT_API_BASE_URL;
        }

        let cvdfApiBaseUrl = defaultUrl;
        console.debug(
            `REACT_CVDF_API_BASE_URL: ${window.__RUNTIME_CONFIG__.REACT_CVDF_API_BASE_URL}`
        );
        if (window.__RUNTIME_CONFIG__.REACT_CVDF_API_BASE_URL) {
            cvdfApiBaseUrl = window.__RUNTIME_CONFIG__.REACT_CVDF_API_BASE_URL;
        }

        let cvdfRealTimeBaseUrl = defaultUrl;
        console.debug(
            `REACT_CVDF_REALTIME_BASE_URL: ${window.__RUNTIME_CONFIG__.REACT_CVDF_REALTIME_BASE_URL}`
        );
        if (window.__RUNTIME_CONFIG__.REACT_CVDF_REALTIME_BASE_URL) {
            cvdfRealTimeBaseUrl = window.__RUNTIME_CONFIG__.REACT_CVDF_REALTIME_BASE_URL;
        }

        const configPath =
            // create-react-app defines process.env.NODE_ENV which is accessible anywhere in the app
            !process.env.NODE_ENV || process.env.NODE_ENV === "development"
                ? urlJoin(resourcesBaseUrl, "appconfig.dev.json")
                : urlJoin(resourcesBaseUrl, "appconfig.json");

        const response = await fetch(configPath, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        const config = await response.json();
        const logosBaseUrl = urlJoin(resourcesBaseUrl, "assets/");

        let ownerLogoUrl: string | undefined = urlJoin(
            logosBaseUrl,
            "Owner_Logo_Transparent.png"
        ).toString();
        ownerLogoUrl = (await fetch(ownerLogoUrl, { method: "HEAD" })
            .then((res) => res.blob())
            .then((blob) => (blob.type.includes("image/") ? true : false))
            .catch(() => false))
            ? ownerLogoUrl
            : undefined;

        config["logos"] = {
            owner: ownerLogoUrl,
            application: urlJoin(
                logosBaseUrl,
                "App_Logo_Transparent.png"
            ).toString(),
        };
        config["theme"] = urlJoin(
            resourcesBaseUrl,
            "themes/theme.css"
        ).toString();
        config["loginRedirectUrl"] = loginRedirectUrl;
        config["logoutRedirectUrl"] = logoutRedirectUrl;

        // If appconfig api urls are set, e.g., for development, use them.
        // Otherwise, load from the env/default value.
        if (!config["apiBaseUrl"]) {
            config["apiBaseUrl"] = apiBaseUrl;
        }
        console.debug("apiBaseUrl: ", config["apiBaseUrl"]);

        if (!config["cvdfApiBaseUrl"]) {
            config["cvdfApiBaseUrl"] = cvdfApiBaseUrl;
        }
        console.debug("cvdfApiBaseUrl: ", config["cvdfApiBaseUrl"]);

        if (!config["cvdfRealTimeBaseUrl"]) {
            config["cvdfRealTimeBaseUrl"] = cvdfRealTimeBaseUrl;
        }
        console.debug("cvdfRealTimeBaseUrl: ", config["cvdfRealTimeBaseUrl"]);

        if (!config["mapCenter"]) {
            config["mapCenter"] = { lat: 28.5, lon: -81.88 };
        }

        return new Config(config);
    };
}
